'use client';

import { c as _c } from "react/compiler-runtime";
import { loader, type DiffEditorProps, type EditorProps } from '@monaco-editor/react';
import type * as monacoEditor from 'monaco-editor';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { useEnvironment } from '@/contexts/useEnvironment';
export type { editor, IPosition } from 'monaco-editor';
export type Monaco = typeof monacoEditor;
let configured = false;
// Point Monaco's AMD loader at the self-hosted `vs/` assets. Must run before the
// editor mounts; idempotent since the prefix is stable for a session.
const configureMonacoLoader = (consoleAssetPrefix: string) => {
  if (configured) {
    return;
  }
  configured = true;
  loader.config({
    paths: {
      vs: `${consoleAssetPrefix}/statics/monaco/vs`
    }
  });
};
const LazyEditor = dynamic<EditorProps>(async () => (await import('@monaco-editor/react')).default, {
  ssr: false
});
const LazyDiffEditor = dynamic<DiffEditorProps>(async () => (await import('@monaco-editor/react')).DiffEditor, {
  ssr: false
});

// Thin wrappers so the AMD loader asset prefix is sourced from the environment
// context (useEnvironment) rather than a direct process.env read. The lazy
// `useState` initializer runs the (idempotent) loader config exactly once per
// mount, before the inner editor's `loader.init()` fires — without a bare side
// effect in the render body.
export const MonacoEditor = props => {
  const $ = _c(4);
  const {
    consoleAssetPrefix
  } = useEnvironment();
  let t0;
  if ($[0] !== consoleAssetPrefix) {
    t0 = () => configureMonacoLoader(consoleAssetPrefix);
    $[0] = consoleAssetPrefix;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useState(t0);
  let t1;
  if ($[2] !== props) {
    t1 = <LazyEditor {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export const MonacoDiffEditor = props => {
  const $ = _c(4);
  const {
    consoleAssetPrefix
  } = useEnvironment();
  let t0;
  if ($[0] !== consoleAssetPrefix) {
    t0 = () => configureMonacoLoader(consoleAssetPrefix);
    $[0] = consoleAssetPrefix;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useState(t0);
  let t1;
  if ($[2] !== props) {
    t1 = <LazyDiffEditor {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export const DIFF_EDITOR_OPTIONS = {
  readOnly: true,
  renderSideBySide: true,
  minimap: {
    enabled: false
  },
  scrollBeyondLastLine: false,
  fontSize: 13,
  // Keep models alive across React Strict Mode double-mounts and prop churn;
  // without this Monaco can throw "TextModel got disposed before
  // DiffEditorWidget model got reset" when the component remounts.
  originalEditable: false
} as const;